import { PayloadAction } from '@reduxjs/toolkit';
import dotPropImmutable from 'dot-prop-immutable';
import get from 'lodash/get';
import {
  DocumentResourceType,
  ResourceSection,
  ResourceSectionType,
} from 'data/documents';
import { ProjectTemplateTask } from 'data/templates/projects';
import {
  CreateDocumentHighlightTaskPayload,
  DeleteDocumentTaskPayload,
} from '../actions';
import {
  DocumentTemplateStore,
  DocumentTemplateContentSection,
} from '../types';

export const documentTemplateTaskSlice = {
  updateDocumentTemplateTask: (
    state: DocumentTemplateStore,
    {
      payload,
    }: PayloadAction<{
      docId: number;
      resourceSectionId: number;
      resourceType?: DocumentResourceType;
      resourceSectionName?: string;
      task: AtLeast<ProjectTemplateTask, 'taskId'>;
    }>,
  ): DocumentTemplateStore => {
    const {
      resourceSectionId,
      task,
      docId,
      resourceSectionName,
      resourceType,
    } = payload;
    const sections: DocumentTemplateContentSection[] = Object.values(
      dotPropImmutable.get(state, `${docId}.sections`, {}),
    );

    const sectionId = sections.find((section) => {
      return (
        get(section, 'meta.resourceSections') as unknown[] as number[]
      )?.includes(resourceSectionId);
    })?.documentSectionId;

    const resourceSection: ResourceSection<Partial<ProjectTemplateTask>> =
      dotPropImmutable.get(
        state,
        `${docId}.resourceSections.${resourceSectionId}`,
        null,
      );
    let result = state;

    if (resourceSection != null) {
      const resourceObjectIndex = (
        resourceSection.resourceObjects || []
      ).findIndex((object) => object.taskId === task.taskId);

      const resourceObjects: Partial<ProjectTemplateTask>[] =
        resourceObjectIndex > -1
          ? (resourceSection.resourceObjects || []).map((object) =>
              object.taskId === task.taskId
                ? {
                    ...object,
                    ...task,
                  }
                : object,
            )
          : [...(resourceSection.resourceObjects || []), task];
      const resourceObjectIds = resourceObjects.map((object) => object.taskId);

      result = dotPropImmutable.set(
        result,
        `${docId}.resourceSections.${resourceSectionId}.resourceIds`,
        resourceObjectIds,
      );
      result = dotPropImmutable.set(
        result,
        `${docId}.resourceSections.${resourceSectionId}.resourceObjects`,
        resourceObjects,
      );
    } else {
      const newResourceSection = {
        resourceSectionId,
        resourceSectionName,
        resourceSectionType: ResourceSectionType.Task,
        resourceType,
        resourceIds: [task.taskId],
        resourceObjects: [task],
      };

      result = dotPropImmutable.merge(
        result,
        `${docId}.sections.${sectionId}.meta.resourceSections`,
        [resourceSectionId],
      );
      result = dotPropImmutable.merge(
        result,
        `${docId}.resourceSections.${resourceSectionId}`,
        newResourceSection,
      );
    }

    return result;
  },

  deleteDocumentTemplateTask: (
    state: DocumentTemplateStore,
    { payload }: PayloadAction<DeleteDocumentTaskPayload>,
  ): DocumentTemplateStore => {
    const { docId, resourceSectionId, taskId } = payload;
    const path = `${docId}.resourceSections.${resourceSectionId}`;

    const resourceObjects = dotPropImmutable.get(
      state,
      `${path}.resourceObjects`,
      [],
    );

    const result = dotPropImmutable.set(
      state,
      `${path}.resourceObjects`,
      resourceObjects.filter((object) => object.taskId !== taskId),
    );

    return dotPropImmutable.set(
      result,
      `${path}.resourceIds`,
      resourceObjects.map((object) => object.taskId),
    );
  },

  createDocumentTemplateHighlightTask: (
    state: DocumentTemplateStore,
    { payload }: PayloadAction<CreateDocumentHighlightTaskPayload>,
  ): DocumentTemplateStore => {
    const { docId, resourceSectionId, sectionId, task } = payload;

    const newResourceSection = {
      resourceSectionId,
      resourceSectionName: 'Highlighted tasks',
      resourceSectionType: ResourceSectionType.Task,
      resourceType: DocumentResourceType.Highlight,
      resourceIds: [task.taskId],
      resourceObjects: [task],
    };

    let result = dotPropImmutable.merge(
      state,
      `${docId}.sections.${sectionId}.meta.resourceSections`,
      [resourceSectionId],
    );

    result = dotPropImmutable.merge(
      result,
      `${docId}.resourceSections.${resourceSectionId}`,
      newResourceSection,
    );

    return result;
  },
};
